import { useCallback, useSyncExternalStore } from 'react';

import { fg } from '@atlaskit/platform-feature-flags';
import { useAIEventsInstrumentation } from '@atlassian/ai-analytics';

import { filterCommonAttributes } from '../../../../ai-answer-dialog/analytics-helpers';
import type { commonAttributesType } from '../../../../ai-answer-dialog/types';
import { getSnapshot, markFired, subscribe } from '../sain-usage-event-store';

export const SAIN_USAGE_DWELL_THRESHOLD = 3000;

export enum SainAction {
	SOURCE_CLICKED = 'sourceLinkClicked',
	POSITIVE_FEEDBACK_PROVIDED = 'positiveFeedbackProvided',
	NEGATIVE_FEEDBACK_PROVIDED = 'negativeFeedbackProvided',
	DWELL_REACHED = 'dwellTimeReached',
	COPY_CLICKED = 'copyButtonClicked',
	FOLLOWUP_CLICKED = 'followupLinkClicked',
	ERROR_STATE_SUGGESTED_QUERY_CLICKED = 'errorStateSuggestedQueryClicked',
	OPEN_CHAT_CLICKED = 'openChatButtonClicked',
	EXPAND_CLICKED = 'expandButtonClicked',
	READ_MORE_BUTTON_CLICKED = 'readMoreButtonClicked',
	TOPIC_CLICKED = 'topicClicked',
	RECENT_WORK_CLICKED = 'recentWorkClicked',
	COLLABORATOR_CLICKED = 'collaboratorClicked',
	VIEW_PROFILE_CLICKED = 'viewProfileClicked',
	TEAM_MEMBER_CLICKED = 'teamMemberClicked',
	TEAM_LIST_CLICKED = 'teamListClicked',
	TEAM_LIST_ITEM_CLICKED = 'teamListItemClicked',
	VIEW_TEAM_PAGE_CLICKED = 'viewTeamPageClicked',
	REPORTING_LINES_CLICKED = 'reportingLinesClicked',
	PERSON_SELECTED = 'cardClicked',
	BOOKMARK_CLICKED = 'bookmarkClicked',
	CHAT_CLICKED = 'chatClicked',
	JIRA_ISSUE_CLICKED = 'jiraIssueCardClicked',
	JIRA_NL_TO_JQL_CLICKED = 'jiraNlToJqlCardClicked',
	JIRA_NL_TO_JQL_VIEW_ALL = 'viewAllButtonClicked',
	JIRA_NL_TO_JQL_WORK_ITEM_CLICKED = 'workItemClicked',
	SHOW_ON_GOOGLE_MAPS_CLICKED = 'showOnGoogleMapsClicked',
	TOPIC_KNOWLEDGE_CARD_CLICKED = 'topicKnowledgeCardClicked',
	NAVIGATIONAL_SEARCH_RESULT_CARD = 'navigationalSearchResultCardClicked',
	FMP_DWELL_START = 'fmpDwellStart',
	FMP_DWELL_START_EXPANDED = 'fmpDwellStartExpanded',
	FMP_DWELL_REACHED = 'fmpDwellReached',
	FMP_RESULT_EXITED = 'fmpDwellExited',
	AI_MODE_FOLLOWUP_CLICKED = 'aiModeFollowupLinkClicked',
	SHOW_MORE_TOPICS_CLICKED = 'showMoreTopicsClicked',
}

export type SAINUsageEventPayload = {
	metCriteria?: SainAction;
	dwellTime?: number;
	commonAttributes?: commonAttributesType;
	sourceProduct?: string;
};

export const useSAINUsageCriteriaMet: () => {
    fireSAINUsageEvent: ({ metCriteria, dwellTime, commonAttributes, sourceProduct }: SAINUsageEventPayload) => void;
} = () => {
	const { trackAIResultView, trackAIResultAction, trackAIFeedbackSubmit } =
		useAIEventsInstrumentation();
	useSyncExternalStore(subscribe, getSnapshot);

	const fireSAINUsageEvent = useCallback(
		({ metCriteria, dwellTime, commonAttributes, sourceProduct }: SAINUsageEventPayload): void => {
			const sanitizedCommonAttributes = commonAttributes
				? filterCommonAttributes(commonAttributes)
				: {};
			// Handle FMP dwell cases - fire initial event with doesNotMeetMAUCriteria = true
			if (
				metCriteria === SainAction.FMP_DWELL_START ||
				metCriteria === SainAction.FMP_DWELL_START_EXPANDED
			) {
				trackAIResultView({
					doesNotMeetMAUCriteria: true,
					attributes: {
						mauEligibilityCriteria: metCriteria,
						met_fmp_criteria: false,
						sourceProduct,
						...sanitizedCommonAttributes,
					},
				});
				return;
			}

			if (metCriteria === SainAction.FMP_DWELL_REACHED) {
				// If dwell time reaches threshold, fire second event (could eventually count as usage criteria)
				trackAIResultView({
					doesNotMeetMAUCriteria: true, // Keep as true for now, could change based on sizing exercise
					attributes: {
						dwellTime,
						mauEligibilityCriteria: metCriteria,
						met_fmp_criteria: true,
						sourceProduct,
						...sanitizedCommonAttributes,
					},
				});
				return;
			}

			if (metCriteria === SainAction.FMP_RESULT_EXITED) {
				trackAIResultView({
					doesNotMeetMAUCriteria: true, // Keep as true. This viewed event is for QSR sizing & is not used for MAU.
					attributes: {
						action: metCriteria,
						dwellTime,
						sourceProduct,
						trackingMethod: 'fmp',
						...sanitizedCommonAttributes,
					},
				});
				return;
			}

			if (metCriteria) {
				// If usage criteria are provided, emit a viewed event with doesNotMeetMAUCriteria set to false
				// only once per SAIN request
				const requestId = commonAttributes?.extraAttributes?.requestId;
				const doesNotMeetMAUCriteria = fg('kd_fix_sain_mau_instrumentation')
					? requestId
						? getSnapshot().has(requestId)
						: true // new behavior: need a requestId to meet MAU criteria to ensure only one MAU-met event is fired
					: false; // old behavior: always met MAU criteria

				trackAIResultView({
					doesNotMeetMAUCriteria,
					attributes: {
						dwellTime: dwellTime || 0,
						mauEligibilityCriteria: metCriteria,
						sourceProduct,
						...sanitizedCommonAttributes,
					},
				});
				if (!doesNotMeetMAUCriteria) {
					markFired(requestId);
				}
				// In addition, emit an action/feedback event for the action that was met
				// (https://hello.atlassian.net/wiki/x/Vxb5DAE)
				switch (metCriteria) {
					case SainAction.SOURCE_CLICKED:
					case SainAction.COPY_CLICKED:
					case SainAction.FOLLOWUP_CLICKED:
					case SainAction.TOPIC_CLICKED:
					case SainAction.RECENT_WORK_CLICKED:
					case SainAction.COLLABORATOR_CLICKED:
					case SainAction.VIEW_PROFILE_CLICKED:
					case SainAction.TEAM_MEMBER_CLICKED:
					case SainAction.VIEW_TEAM_PAGE_CLICKED:
					case SainAction.OPEN_CHAT_CLICKED:
					case SainAction.JIRA_NL_TO_JQL_VIEW_ALL:
					case SainAction.JIRA_NL_TO_JQL_WORK_ITEM_CLICKED:
						trackAIResultAction(metCriteria, {
							attributes: { dwellTime, sourceProduct, ...sanitizedCommonAttributes },
						});
						break;
					case SainAction.POSITIVE_FEEDBACK_PROVIDED:
						trackAIFeedbackSubmit('up', {
							attributes: { dwellTime, ...sanitizedCommonAttributes },
						});
						break;
					case SainAction.NEGATIVE_FEEDBACK_PROVIDED:
						trackAIFeedbackSubmit('down', {
							attributes: { dwellTime, ...sanitizedCommonAttributes },
						});
						break;
					case SainAction.AI_MODE_FOLLOWUP_CLICKED:
						trackAIResultAction(metCriteria, {
							attributes: { dwellTime, sourceProduct, ...sanitizedCommonAttributes },
						});
						break;
					default:
						break;
				}
			} else {
				// If no usage criteria are provided, emit a viewed event with doesNotMeetMAUCriteria set to true
				trackAIResultView({
					doesNotMeetMAUCriteria: true,
					attributes: {
						dwellTime: dwellTime || 0,
						...sanitizedCommonAttributes,
					},
				});
			}
		},

		[trackAIResultView, trackAIResultAction, trackAIFeedbackSubmit],
	);

	return { fireSAINUsageEvent };
};
